import type { FC } from "react"
import { useCallback, useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconCircleArrowUpOutline18 as CircleArrowUp,
	IconCircleInfoOutline18 as InfoIcon,
} from "nucleo-ui-outline-18"

import type { PaidPlan } from "@productlane/lib/src/plans-client"
import { cn } from "@productlane/lib/src/cn"
import { Button, Dialog, Tooltip } from "@productlane/ui"

import { api } from "@/lib/api"

// Tooltip content for trial banner features
const trialFeatureTooltips: Record<string, string> = {
	"Shared email inbox": "Unified email inbox for team collaboration on support",
	"Linear integration":
		"The most advanced Linear integration in customer support",
	"Live Chat": "Real-time chat support with your team directly from the widget",
	"Self-updating docs":
		"Keeps your docs up to date based on historic conversations, completed Linear issues, and published changelogs",
	"Linear feedback portal":
		"Turn your Linear into a feedback community or just share your roadmap with customers",
	"Custom domain": "Use your own domain for your portal",
	"Shared Slack inbox":
		"Centralize your customer Slack connect channels and answer within Productlane",
	"Productlane Agent":
		"AI-powered support agent that answers customer questions, checks bug reports against your product data, and files the Linear issue",
	"Custom Connectors": "Show live data from your own API next to every thread",
	"Custom theme": "Customize the look and feel of your public pages",
	"Custom views": "Create custom filtered views for your support inbox",
	Reporting: "Track key support metrics and team performance",
	"Customer Support Portal":
		"Self-service portal for customers to find their Linear requests",
	"Multi-Language":
		"Change the language for your public pages, widget, changelog and help center articles",
	"Private portal":
		"Whitelist only certain email domains for access to your portal",
	"SSO (JWT)":
		"Single Sign-On integration with JWTs, so your users don't need to login",
	Snippets: "Pre-written responses and templates for common inquiries",
	"SLA policies":
		"Set service level agreements for response and resolution times",
	"HubSpot integration": "Sync conversations from Productlane to HubSpot",
}

const FeatureWithTooltip = ({
	feature,
	children,
}: {
	feature: string
	children: React.ReactNode
}) => {
	const tooltipContent = trialFeatureTooltips[feature]

	if (!tooltipContent) {
		return <div className="flex items-center gap-1">{children}</div>
	}

	return (
		<div className="flex items-center gap-1">
			{children}
			<Tooltip
				label={tooltipContent}
				contentClassName="max-w-[50rem] w-full"
				side="right"
			>
				<InfoIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
			</Tooltip>
		</div>
	)
}

interface TrialBannerProps {
	daysLeft: number
}

const plans: {
	plan: PaidPlan
	price: number
	title: string
	benefits: string[]
}[] = [
	{
		plan: "starter",
		price: 29,
		title: "What you get",
		benefits: [
			"Shared email inbox",
			"Linear integration",
			"Live Chat",
			"Self-updating docs",
			"Linear feedback portal",
			"Custom domain",
		],
	},
	{
		plan: "pro",
		price: 59,
		title: "All Starter features, plus",
		benefits: [
			"Shared Slack inbox",
			"Productlane Agent",
			"Custom Connectors",
			"Custom theme",
			"Custom views",
			"Reporting",
			"Macros",
		],
	},
	{
		plan: "scale",
		price: 99,
		title: "All Pro features, plus",
		benefits: [
			"Customer Support Portal",
			"Multi-Language",
			"Private portal",
			"SSO (JWT)",
			"SLA policies",
			"HubSpot integration",
		],
	},
] as const

export const TrialBanner: FC<TrialBannerProps> = ({ daysLeft }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [selected, setSelection] = useState(1)

	const onClick = useCallback(() => {
		setIsOpen(true)
	}, [])

	const createUpdatePlanMutation = api.workspace.updatePlan.useMutation()

	const buyPlan = useCallback(async () => {
		const selectedPlan = plans[selected]
		if (!selectedPlan) return
		const url = await createUpdatePlanMutation.mutateAsync({
			plan: selectedPlan.plan,
			interval: "yearly",
		})

		if (url) {
			location.href = url
		}
	}, [createUpdatePlanMutation, selected])

	return (
		<>
			<Dialog
				isOpen={isOpen}
				onOpenChange={setIsOpen}
				size="xl"
				title={
					<div>
						<CircleArrowUp
							className="mb-3 text-green-500 dark:bg-gray-900"
							strokeWidth={1}
						/>
						<div className="text-3xl font-medium">
							Purchase Productlane today
						</div>
					</div>
				}
			>
				<div className="mb-8 text-sm text-secondary">
					Upgrade your plan to set up the most efficient customer support system
					for companies on Linear.
				</div>
				<div className="mb-8 mt-4 flex justify-between gap-3">
					{plans.map(({ plan, price, title, benefits }, index) => (
						<button
							key={`plan-${index}`}
							className={cn(
								"min-h-full w-full rounded-2xl border  bg-white px-6 pb-4 pt-8 text-left hover:border-green-500/50 dark:border-gray-700 dark:bg-gray-900 dark:bg-transparent dark:hover:border-green-500/50",
								{
									"border-green-500 bg-green-50/50 shadow-sm hover:border-green-500 dark:border-green-500 dark:bg-gray-800 dark:hover:border-green-500":
										selected === index,
								},
							)}
							onClick={() => setSelection(index)}
						>
							<div className="mb-2 text-xl">{`${plan[0]?.toUpperCase()}${plan.slice(
								1,
							)}`}</div>
							<div>
								<span className="text-3xl font-medium text-primary">
									${price}
								</span>
								<span className="text-sm text-tertiary"> per user / month</span>
							</div>
							<div className="mb-4 mt-6 font-medium">{title}:</div>
							<ul>
								{benefits.map((benefit, index) => (
									<li
										key={`benefit-${index}`}
										className="my-2 flex list-none text-sm text-secondary"
									>
										<Check
											className={cn("mr-2 h-5 w-5 text-green-500", {
												"opacity-0": !benefit,
											})}
										/>{" "}
										<FeatureWithTooltip feature={benefit}>
											<span>{benefit}</span>
										</FeatureWithTooltip>
									</li>
								))}
							</ul>
						</button>
					))}
				</div>
				<div className="flex items-center justify-end gap-2">
					<Button
						variant="outline"
						onClick={() => {
							window.open(
								"https://productlane.com/startup",
								"_blank",
								"noopener,noreferrer",
							)
						}}
					>
						Apply for startup discount
					</Button>
					<Button onClick={buyPlan}>
						Buy{" "}
						{`${plans[selected]?.plan[0]?.toUpperCase()}${plans[
							selected
						]?.plan.slice(1)}`}{" "}
						now from ${plans[selected]?.price} / editor
					</Button>
				</div>
			</Dialog>
			<div className="w-full px-2">
				<button
					onClick={onClick}
					className="group relative mb-2 mt-0 flex w-full select-none items-center justify-between rounded-lg bg-white px-3 py-2 shadow-sm ring-1 ring-black/5 hover:ring-black/15 dark:bg-gray-900 dark:shadow-black dark:ring-white/10 dark:hover:bg-gray-800"
				>
					<span className="text-sm">
						{`${daysLeft} day${daysLeft > 1 ? "s" : ""} left in trial`}
					</span>
					<CircleArrowUp
						className="h-4 w-4 text-green-600 transition-colors"
						strokeWidth={1}
					/>
				</button>
			</div>
		</>
	)
}
